import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { SlidersHorizontal, X } from "lucide-react";
import api from "../lib/api";
import { PROPERTY_TYPES, typeLabel } from "../lib/format";
import PropertyCard from "../components/PropertyCard";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "../components/ui/sheet";
import Seo from "../components/Seo";

const specialFlags = [
  { key: "personally_verified", label: "Verified" },
  { key: "price_reduced", label: "Price Reduced" },
  { key: "urgent_sale", label: "Urgent Sale" },
  { key: "featured", label: "Featured" },
  { key: "video_available", label: "Video" },
];

function Filters({ f, setF, onApply }) {
  const upd = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  return (
    <div className="space-y-5">
      <div>
        <label className="text-sm font-medium text-slate-700">Property Type</label>
        <Select value={f.property_type || "any"} onValueChange={(v) => upd("property_type", v === "any" ? "" : v)}>
          <SelectTrigger className="mt-1.5" data-testid="filter-type"><SelectValue placeholder="Any" /></SelectTrigger>
          <SelectContent><SelectItem value="any">Any Type</SelectItem>{PROPERTY_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><label className="text-sm font-medium text-slate-700">Min Price (₹)</label>
          <input type="number" value={f.min_price || ""} onChange={(e) => upd("min_price", e.target.value)} data-testid="filter-min-price" className="mt-1.5 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" /></div>
        <div><label className="text-sm font-medium text-slate-700">Max Price (₹)</label>
          <input type="number" value={f.max_price || ""} onChange={(e) => upd("max_price", e.target.value)} data-testid="filter-max-price" className="mt-1.5 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" /></div>
      </div>
      <div>
        <label className="text-sm font-medium text-slate-700">Bedrooms</label>
        <div className="flex gap-2 mt-1.5">
          {[1, 2, 3, 4, 5].map((b) => (
            <button key={b} onClick={() => upd("bedrooms", f.bedrooms === String(b) ? "" : String(b))} data-testid={`filter-bhk-${b}`}
              className={`w-10 h-9 rounded-lg text-sm font-medium border ${f.bedrooms === String(b) ? "bg-indigo-900 text-white border-indigo-900" : "border-slate-200 text-slate-600"}`}>{b}{b === 5 ? "+" : ""}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><label className="text-sm font-medium text-slate-700">Min Area</label>
          <input type="number" value={f.min_area || ""} onChange={(e) => upd("min_area", e.target.value)} className="mt-1.5 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" /></div>
        <div><label className="text-sm font-medium text-slate-700">Parking</label>
          <Select value={f.parking || "any"} onValueChange={(v) => upd("parking", v === "any" ? "" : v)}>
            <SelectTrigger className="mt-1.5"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent><SelectItem value="any">Any</SelectItem><SelectItem value="0">0</SelectItem><SelectItem value="1">1+</SelectItem><SelectItem value="2">2+</SelectItem></SelectContent>
          </Select></div>
      </div>
      <div>
        <label className="text-sm font-medium text-slate-700">Special</label>
        <div className="flex flex-wrap gap-2 mt-1.5">
          {specialFlags.map((s) => (
            <button key={s.key} onClick={() => upd(s.key, f[s.key] ? "" : "true")} data-testid={`filter-${s.key}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border ${f[s.key] ? "bg-indigo-50 text-indigo-800 border-indigo-300" : "border-slate-200 text-slate-600"}`}>{s.label}</button>
          ))}
        </div>
      </div>
      <Button onClick={onApply} data-testid="apply-filters" className="w-full bg-indigo-900 hover:bg-indigo-800">Apply Filters</Button>
    </div>
  );
}

export default function Properties({ preset, special, title }) {
  const [sp, setSp] = useSearchParams();
  const initial = () => {
    const obj = Object.fromEntries(sp.entries());
    if (preset) obj.property_type = preset;
    if (special) obj[special] = "true";
    return obj;
  };
  const [f, setF] = useState(initial);
  const [applied, setApplied] = useState(initial);
  const [sort, setSort] = useState("newest");

  useEffect(() => { const v = initial(); setF(v); setApplied(v); /* eslint-disable-next-line */ }, [preset, special, sp]);

  const { data, isLoading } = useQuery({
    queryKey: ["properties", applied, sort],
    queryFn: async () => (await api.get("/properties", { params: { ...applied, sort, limit: 24 } })).data,
  });

  const apply = () => {
    setApplied(f);
    const clean = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    setSp(clean);
  };

  const heading = title || (preset ? `${typeLabel(preset)}s in Faridabad` : "All Properties in Faridabad");

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Seo title={`${heading} | Faridabad Property Vault`} description={`Browse ${heading.toLowerCase()} — personally verified and honestly priced.`} />
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-4xl font-extrabold text-slate-900">{heading}</h1>
          <p className="text-slate-500 mt-1">{data?.total ?? 0} verified {data?.total === 1 ? "property" : "properties"}</p>
        </div>
        <div className="flex items-center gap-2">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" className="lg:hidden" data-testid="mobile-filter-btn"><SlidersHorizontal className="w-4 h-4 mr-2" /> Filters</Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[320px] overflow-y-auto">
              <SheetHeader><SheetTitle>Filters</SheetTitle></SheetHeader>
              <div className="mt-6"><Filters f={f} setF={setF} onApply={apply} /></div>
            </SheetContent>
          </Sheet>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[180px]" data-testid="sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="price_low">Price: Low to High</SelectItem>
              <SelectItem value="price_high">Price: High to Low</SelectItem>
              <SelectItem value="area">Area</SelectItem>
              <SelectItem value="score">Property Score</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        <aside className="hidden lg:block">
          <div className="sticky top-24 bg-white rounded-2xl border border-slate-200 p-5">
            <h3 className="font-heading font-bold text-slate-900 mb-4 flex items-center gap-2"><SlidersHorizontal className="w-4 h-4" /> Filters</h3>
            <Filters f={f} setF={setF} onApply={apply} />
          </div>
        </aside>
        <div>
          {isLoading ? (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-96 rounded-2xl bg-slate-100 animate-pulse" />)}</div>
          ) : data?.items?.length ? (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="properties-grid">{data.items.map((p) => <PropertyCard key={p.id} p={p} />)}</div>
          ) : (
            <div className="text-center py-24 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
              <p className="font-heading text-lg font-semibold text-slate-700">No properties match your filters</p>
              <p className="text-slate-500 text-sm mt-1">Try adjusting your budget or property type.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
